import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowDown, FaGithub, FaLinkedin } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import ParticleBackground from '../components/ParticleBackground';
import Button from '../components/Button';
import profileImage from './Display_picture.jpg';

const roles = [
  'Full Stack Developer',
  'MERN Stack Engineer',
  'Competitive Programmer',
  'Cloud & DevOps Enthusiast',
];

const Home = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentRole = roles[roleIndex];
    let timeout;

    if (!isDeleting && displayText === currentRole) {
      timeout = setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && displayText === '') {
      setIsDeleting(false);
      setRoleIndex((prev) => (prev + 1) % roles.length);
    } else {
      timeout = setTimeout(() => {
        setDisplayText(
          isDeleting
            ? currentRole.slice(0, displayText.length - 1)
            : currentRole.slice(0, displayText.length + 1)
        );
      }, isDeleting ? 40 : 80);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, roleIndex]);

  const scrollToAbout = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroSection id="home">
      <ParticleBackground />

      {/* Ambient glow orbs */}
      <GlowOrb1 />
      <GlowOrb2 />

      <HeroContent>
        <HeroGrid>
          <HeroText>
            <Greeting
              as={motion.p}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <GreetingLine /> Hey there, I'm
            </Greeting>

            <HeroName
              as={motion.h1}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              Himanshu Rawat
            </HeroName>

            <RoleWrapper
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <RolePrefix>I build things as a</RolePrefix>
              <TypedRole>
                {displayText}
                <Cursor />
              </TypedRole>
            </RoleWrapper>

            <HeroDescription
              as={motion.p}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              Crafting scalable web applications with the MERN stack, AWS cloud infrastructure,
              and a passion for clean, performant code. Currently focused on building
              enterprise-grade attendance systems and AI-powered tools.
            </HeroDescription>

            <HeroActions
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Button
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                size="large"
                glow
              >
                View My Work
              </Button>
              <Button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                size="large"
              >
                Let's Talk
              </Button>
            </HeroActions>

            <HeroSocials
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <SocialIcon href="https://github.com/HimanshuRawat143" target="_blank" rel="noopener noreferrer">
                <FaGithub />
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/in/himanshurawat12" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </SocialIcon>
              <SocialIcon href="mailto:himu35311@gmail.com">
                <HiOutlineMail />
              </SocialIcon>
              <SocialLine />
            </HeroSocials>
          </HeroText>

          <HeroImageWrapper
            as={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <ImageRing>
              <ProfileImg src={profileImage} alt="Himanshu Rawat" />
            </ImageRing>
            <StatusBadge>
              <StatusDot /> Available for work
            </StatusBadge>
          </HeroImageWrapper>
        </HeroGrid>
      </HeroContent>

      <ScrollIndicator onClick={scrollToAbout}>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <FaArrowDown />
        </motion.div>
      </ScrollIndicator>
    </HeroSection>
  );
};

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 72px;
`;

const GlowOrb1 = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
  top: -10%;
  left: -10%;
  animation: ${pulseGlow} 8s ease-in-out infinite;
  pointer-events: none;
`;

const GlowOrb2 = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, transparent 70%);
  bottom: -5%;
  right: -5%;
  animation: ${pulseGlow} 10s ease-in-out infinite 2s;
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 92%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2.5rem;
  }
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    gap: 2rem;
  }
`;

const HeroText = styled.div`
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    order: 2;
  }
`;

const GreetingLine = styled.span`
  display: inline-block;
  width: 40px;
  height: 2px;
  background: ${props => props.theme.colors.primary};
  margin-right: 12px;
  vertical-align: middle;
`;

const Greeting = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  font-family: ${props => props.theme.fonts.mono};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
`;

const HeroName = styled.h1`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1.1;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f1f5f9 30%, #6366f1 70%, #06b6d4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    letter-spacing: -1px;
  }
`;

const RoleWrapper = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
`;

const RolePrefix = styled.span`
  font-size: 1.15rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 400;

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

const TypedRole = styled.span`
  font-size: 1.15rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.mono};

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 0.95rem;
  }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.2em;
  background-color: ${props => props.theme.colors.accent};
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: ${blink} 1s step-end infinite;
`;

const HeroDescription = styled.p`
  font-size: 1.05rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.8;
  max-width: 560px;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin: 0 auto 2rem;
  }
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 0.92rem;
    line-height: 1.7;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const HeroSocials = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  color: ${props => props.theme.colors.textMuted};
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const SocialLine = styled.div`
  width: 60px;
  height: 1px;
  background: ${props => props.theme.colors.textMuted};
  opacity: 0.3;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const HeroImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    order: 1;
  }
`;

const ImageRing = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  padding: 4px;
  background: ${props => props.theme.colors.gradientPrimary};
  animation: ${float} 6s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 1px dashed rgba(99, 102, 241, 0.3);
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 200px;
    height: 200px;
  }
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 180px;
    height: 180px;
    animation: none;
  }
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${props => props.theme.colors.background};
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: #0c0c24;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 30px;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${props => props.theme.fonts.mono};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
`;

const ScrollIndicator = styled.button`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.textMuted};
  font-size: 1rem;
  cursor: pointer;
  z-index: 3;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

export default Home;